import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import SimpleLoader from '../components/SimpleLoader';
import { getFileOpeningRegisterEntries } from '../services/fileOpenings';
import toast from 'react-hot-toast';

import { deleteFileOpening } from '../services/fileOpenings';

import ConfirmationModal from '../components/ConfirmationModal';
import { PDFViewer } from '@react-pdf/renderer';
import FileOpeningRegisterPdf from '../components/pdf/FileOpeningRegisterPdf';
import FileOpeningRegisterPdfButton from '../components/pdf/FileOpeningRegisterPdfButton';
import Pagination from '../components/Pagination';
import TableContainer from '../components/TableContainer';

const FileOpeningRegister = () => {
  const navigate = useNavigate();
  const [fileOpenings, setFileOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const fetchFileOpenings = async () => {
      try {
        const offset = (currentPage - 1) * limit;
        const { fileOpenings, count } = await getFileOpeningRegisterEntries({ limit, offset, sortBy, sortOrder, searchTerm, startDate, endDate });
        setFileOpenings(fileOpenings);
        setTotal(count);
        setPreviewKey(prev => prev + 1); // Force PDF preview to refresh
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchFileOpenings();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [limit, currentPage, sortBy, sortOrder, searchTerm, startDate, endDate]);

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handleSortByChange = (e) => {
    const [newSortBy, newSortOrder] = e.target.value.split('_');
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };



  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setCurrentPage(1);
  };

  const closeModal = () => {
    setSelectedFileId(null);
    setIsModalOpen(false);
  };

  const handleDelete = async () => {
    if (selectedFileId) {
      try {
        await deleteFileOpening(selectedFileId);
        toast.success('File opening deleted successfully!');
        // Refetch the data to update the list
        const offset = (currentPage - 1) * limit;
        const { fileOpenings, count } = await getFileOpeningRegisterEntries({ limit, offset, sortBy, sortOrder, searchTerm, startDate, endDate });
        setFileOpenings(fileOpenings);
        setTotal(count);
      } catch (error) {
        toast.error(error.message);
      }
      closeModal();
    }
  };

  return (
    <DashboardLayout pageTitle="File Opening Register">
      <div className='flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4'>
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
            File Opening Register
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow text-sm"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => setShowPdfPreview(!showPdfPreview)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow text-sm"
          >
            {showPdfPreview ? 'Hide PDF Preview' : 'Show PDF Preview'}
          </button>
          <FileOpeningRegisterPdfButton data={fileOpenings} key={previewKey} />
          <Link
            to="/file-opening/create"
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow text-sm"
          >
            New File Opening
          </Link>
        </div>
      </div>
      <div className='mb-4'>
        {showPdfPreview && fileOpenings && (
          <div style={{ height: '80vh' }} className="mb-4 text-right">
            <PDFViewer width="100%" height="100%">
              <FileOpeningRegisterPdf data={fileOpenings} key={previewKey} />
            </PDFViewer>
          </div>
        )}
      </div>
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                    <input
                        type="text"
                        placeholder="Description, reference, name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    />
                </div>
                
                <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Show</label>
                    <select
                        value={limit}
                        onChange={handleLimitChange}
                        className="dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2 w-full"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                </div>

                <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sort By</label>
                    <select
                        value={`${sortBy}_${sortOrder}`}
                        onChange={handleSortByChange}
                        className="dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2 w-full"
                    >
                        <option value="id_desc">ID (Z-A)</option>
                        <option value="id_asc">ID (A-Z)</option>
                        <option value="date_desc">Date (Newest First)</option>
                        <option value="date_asc">Date (Oldest First)</option>
                        <option value="forename_asc">Client (A-Z)</option>
                        <option value="forename_desc">Client (Z-A)</option>
                    </select>
                </div>

                {/* Date Range Filters - 4th column with 50% each */}
                <div className="flex flex-col justify-end sm:col-span-2 lg:col-span-1">
                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="flex flex-col flex-1 sm:flex-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={handleStartDateChange}
                                className="dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2 w-full"
                            />
                        </div>
                        <div className="flex flex-col flex-1 sm:flex-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={handleEndDateChange}
                                className="dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md p-2 w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>

      <TableContainer>
        {loading ? (
          <SimpleLoader />
        ) : (
          <>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[800px] text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-6 py-3">Date</th>
                  <th scope="col" className="px-6 py-3">Client</th>
                  <th scope="col" className="px-6 py-3">Date Of Birth</th>
                  <th scope="col" className="px-6 py-3">File Reference</th>
                  <th scope="col" className="px-6 py-3">Address</th>
                  <th scope="col" className="px-6 py-3">Matter</th>
                  <th scope="col" className="px-6 py-3">ID Check</th>
                </tr>
              </thead>
              <tbody>
                {fileOpenings.map((file) => (
                  <tr key={file.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4">{file.date}</td>
                    <td className="px-6 py-4">{`${file.title} ${file.forename} ${file.surname}`}</td>
                    <td className="px-6 py-4">{file.dob}</td>
                    <td className="px-6 py-4">{"AA/SK/BHAM/" + file.id}</td>
                    <td className="px-6 py-4">
                      <p className={!file.number_street ? 'hidden' : ""}>{file.number_street + ","}</p>
                      <p className={!file.city ? 'hidden' : ""}>{file.city + ','}</p>
                      <p className={!file.country ? 'hidden' : ""}>{file.country + ','}</p>
                      <p className={!file.postal_code ? 'hidden' : ""}>{file.postal_code}</p>
                    </td>
                    <td className="px-6 py-4">
                      {file.matter_type || "Not Defined"}
                    </td>
                    <td className="px-6 py-4">
                      {file.id_carried_out ? file.evidence_relied_upon || "Not defined" : 'No Identification Carried Out'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <Pagination
              currentPage={currentPage}
              total={total}
              limit={limit}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </TableContainer>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this file opening?"
      />
    </DashboardLayout>
  );
};

export default FileOpeningRegister;
